/**
 * Table export.
 *
 * Reproduces the five DataTables Buttons actions the legacy portals exposed
 * (`dom: "Blfrtip"` with copy/csv/excel/pdf/print) without pulling in
 * DataTables, jQuery, JSZip and pdfmake — which was roughly 400 KB of
 * dependencies, including pdfmake 0.1.53 from 2018.
 *
 * Excel export writes SpreadsheetML 2003, an XML dialect Excel opens natively.
 * That avoids a ZIP/xlsx writer entirely while still producing a real
 * spreadsheet rather than a CSV with an .xls extension.
 *
 * PDF is delegated to the browser's print-to-PDF, which renders German
 * characters correctly — the legacy pdfmake build used a font subset that
 * mangled umlauts in exported lead lists.
 */

export interface ExportColumn {
  header: string;
  /** Plain-text value for this row; never markup. */
  value: (row: never) => string;
}

export interface ExportPayload {
  fileName: string;
  title: string;
  headers: string[];
  rows: string[][];
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const timestamp = (): string => new Date().toISOString().slice(0, 10);

/** Copies the table to the clipboard as TSV, which pastes into Excel cleanly. */
export async function exportCopy(payload: ExportPayload): Promise<void> {
  const text = [payload.headers, ...payload.rows]
    .map((row) => row.map((cell) => cell.replace(/\t|\r?\n/g, ' ')).join('\t'))
    .join('\n');

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard API needs a secure context and permission; fall back to the
    // legacy selection trick so the button still works over plain HTTP.
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    area.remove();
  }
}

function csvCell(value: string): string {
  // Guard against formula injection: a cell starting with = + - @ is executed
  // by Excel on open. Prefixing with a quote neutralises it.
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}

export function exportCsv(payload: ExportPayload): void {
  const csv = [payload.headers, ...payload.rows]
    .map((row) => row.map(csvCell).join(';'))
    .join('\r\n');

  // BOM + semicolons: what German Excel expects for a UTF-8 CSV.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${payload.fileName}-${timestamp()}.csv`);
}

const xmlEscape = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** SpreadsheetML 2003 — a real workbook Excel opens without a converter. */
export function exportExcel(payload: ExportPayload): void {
  const row = (cells: string[], styleId?: string): string =>
    `<Row>${cells
      .map(
        (cell) =>
          `<Cell${styleId ? ` ss:StyleID="${styleId}"` : ''}><Data ss:Type="String">${xmlEscape(
            cell,
          )}</Data></Cell>`,
      )
      .join('')}</Row>`;

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Styles>
    <Style ss:ID="header">
      <Font ss:Bold="1"/>
      <Interior ss:Color="#E9ECEF" ss:Pattern="Solid"/>
    </Style>
  </Styles>
  <Worksheet ss:Name="${xmlEscape(payload.title).slice(0, 31)}">
    <Table>
      ${row(payload.headers, 'header')}
      ${payload.rows.map((r) => row(r)).join('\n      ')}
    </Table>
  </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  triggerDownload(blob, `${payload.fileName}-${timestamp()}.xls`);
}

/**
 * Opens a clean, print-styled copy of the table. Used for both Print and PDF —
 * the browser's own "Save as PDF" handles the latter, and unlike the legacy
 * pdfmake build it renders umlauts correctly.
 */
export function exportPrint(payload: ExportPayload): void {
  const win = window.open('', '_blank', 'width=1024,height=768');
  if (!win) return;

  const table = `
    <table>
      <thead><tr>${payload.headers.map((h) => `<th>${xmlEscape(h)}</th>`).join('')}</tr></thead>
      <tbody>${payload.rows
        .map((r) => `<tr>${r.map((c) => `<td>${xmlEscape(c)}</td>`).join('')}</tr>`)
        .join('')}</tbody>
    </table>`;

  win.document.write(`<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${xmlEscape(payload.title)}</title>
<style>
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; margin: 24px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  th, td { border: 1px solid #ccc; padding: 5px 7px; text-align: left; vertical-align: top; }
  th { background: #eef1f4; font-weight: 600; }
  tr { page-break-inside: avoid; }
  @page { size: landscape; margin: 12mm; }
</style></head>
<body>
  <h1>${xmlEscape(payload.title)}</h1>
  <div class="meta">Insurensa &middot; ${new Date().toLocaleString('de-DE')} &middot; ${payload.rows.length} Einträge</div>
  ${table}
</body></html>`);

  win.document.close();
  win.focus();
  // Let fonts and layout settle before invoking the print dialog.
  setTimeout(() => win.print(), 300);
}
